import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../button/Button";
import styles from "../../ChangePasswordForm/ChangePasswordForm.module.scss";
import eye from "../../../assets/icons/eye.svg";
import eyeSlash from "../../../assets/icons/eye-slash.svg";
import eyeSlashHover from "../../../assets/icons/eye-slash-hover.svg";
import eyeHover from "../../../assets/icons/eye-hover.svg";
import dot from "../../../assets/icons/circle.svg";
import done from "../../../assets/icons/status-done.svg";
import like from "../../../assets/icons/like.svg";
import close from "../../../assets/icons/close.svg";

import Input from "../../input/Input";
import { useCreateNewPasswordMutation } from "../../../store/services/auth-api";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../../../utils/analytics";
import { EVENT_NAMES } from "../../../types/event-names";

interface ChangePasswordFormProps {
  text?: string;
  isForgotPassword?: boolean;
  setIsVisible?: (value: boolean) => void;
}

const UpdatePasswordForm: React.FC<ChangePasswordFormProps> = ({ isForgotPassword = false, setIsVisible }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [passwordFocused, setPasswordFocused] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  const [confirmFocused, setConfirmFocused] = useState(false);
  const [confirmValue, setConfirmValue] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [createNewPassword] = useCreateNewPasswordMutation();

  const [minLength, setMinLength] = useState(false);
  const [hasUppercase, setHasUppercase] = useState(false);
  const [hasLowercase, setHasLowercase] = useState(false);
  const [hasDigit, setHasDigit] = useState(false);
  const [createdPassword, setCreatedPassword] = useState(false);
  const [isLoader, setIsLoader] = useState(false);

  useEffect(() => {
    if (isForgotPassword) {
      trackEvent(EVENT_NAMES.FORGOT_PASSWORD_VERIFIED);
    }
    const opened = isForgotPassword
      ? EVENT_NAMES.FORGOT_PASSWORD_NEW_PASSWORD_OPENED
      : EVENT_NAMES.CHANGE_PASSWORD_NEW_PASSWORD_OPENED;
    trackEvent(opened);
  }, [isForgotPassword]);

  const onUpdateClickHandler = async () => {
    if (
      minLength &&
      hasUppercase &&
      hasLowercase &&
      hasDigit &&
      !passwordMismatch &&
      passwordValue === confirmValue
    ) {
      try {
        setIsLoader(true);
        if (isForgotPassword) {
          trackEvent(EVENT_NAMES.FORGOT_PASSWORD_NEW_PASSWORD_OPENED);
          await createNewPassword({ new_password: passwordValue })
            .unwrap()
            .then(() => {
              trackEvent(EVENT_NAMES.FORGOT_PASSWORD_RESET_SUCCESS);
              setCreatedPassword(true);
              setIsLoader(false);
            });
        } else {
          trackEvent(EVENT_NAMES.CHANGE_PASSWORD_EMAIL_SUBMITTED);

          await createNewPassword({ new_password: passwordValue })
            .unwrap()
            .then(() => {
              trackEvent(EVENT_NAMES.CHANGE_PASSWORD_RESET_SUCCESS);
              setIsLoader(false);
              navigate("/");
            });
        }
      } catch (e) {
        console.error("Error:", e);
        setIsLoader(false);
      }
    }
  };

  const checkPasswordRequirements = (value: string) => {
    setMinLength(value.length >= 8);
    setHasUppercase(/[A-Z]/.test(value));
    setHasLowercase(/[a-z]/.test(value));
    setHasDigit(/\d/.test(value));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmVisibility = () => {
    setShowConfirm(!showConfirm);
  };

  const handleConfirmChange = (e: any) => {
    setConfirmValue(e.target.value);
    setPasswordMismatch(e.target.value !== passwordValue);
  };

  const handlePasswordChange = (e: any) => {
    setPasswordValue(e.target.value);
    checkPasswordRequirements(e.target.value);
  };

  return (
    <>
      {createdPassword ? (
        <div className={styles.modal}>
          <img src={like} alt="like" className={styles.modal_img} />
          <p className={styles.modal_title}>{t("Password successfully changed")}</p>
          <p className={styles.modal_subtitle}>
            {t("You can now access your Fortect account with your new password.")}
          </p>
          <div className={styles.modal_footer}>
            <Button
              onClick={() => {
                navigate("/");
              }}
              text={t("Login")}
              classNames={styles.modal_footer_btn}
            />
          </div>
        </div>
      ) : (
        <div className={styles.root}>
          {setIsVisible && (
            <img src={close} className={styles.root_close} alt="close" onClick={() => setIsVisible(false)} />
          )}
          <div className={styles.root_header}>
            <h1 className={styles.root_header_title}>{t("Create your new password")}</h1>
            <h2 className={styles.root_header_subtitle}>{t("Enter a new password for the account")}</h2>
          </div>
          <div className={styles.root_main}>
            <div className={styles.root_main_block}>
              <Input
                type={showPassword ? "text" : "password"}
                value={passwordValue}
                onChange={handlePasswordChange}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                focused={passwordFocused}
                placeholder={t("Enter new Password")}
              />
              <img
                src={showPassword ? eyeSlash : eye}
                onMouseOver={(e) => (e.currentTarget.src = showPassword ? eyeSlashHover : eyeHover)}
                onMouseOut={(e) => (e.currentTarget.src = showPassword ? eyeSlash : eye)}
                alt="eye"
                className={styles.root_main_block_eye}
                onClick={togglePasswordVisibility}
              />
            </div>
            <div className={styles.root_main_block}>
              <Input
                type={showConfirm ? "text" : "password"}
                value={confirmValue}
                onChange={handleConfirmChange}
                onFocus={() => setConfirmFocused(true)}
                onBlur={() => setConfirmFocused(false)}
                focused={confirmFocused}
                placeholder={t("Confirm new Password")}
                error={passwordMismatch}
              />
              <img
                src={showConfirm ? eyeSlash : eye}
                onMouseOver={(e) => (e.currentTarget.src = showConfirm ? eyeSlashHover : eyeHover)}
                onMouseOut={(e) => (e.currentTarget.src = showConfirm ? eyeSlash : eye)}
                alt="eye"
                className={styles.root_main_block_eye}
                onClick={toggleConfirmVisibility}
              />
              {passwordMismatch ? (
                <p className={styles.root_main_error}>{t("Password must match.")}</p>
              ) : (
                confirmValue && <p className={styles.root_main_successful}>{t("Password match.")}</p>
              )}
            </div>

            <div className={styles.root_main_rules}>
              <p className={styles.root_main_rules_title}>{t("Your new password should include:")}</p>
              <div className={styles.root_main_rules_list}>
                <div className={styles.root_main_rules_list_item}>
                  <div className={styles.root_main_rules_list_item_img}>
                    <img src={minLength ? done : dot} alt="dot" />
                  </div>
                  <p>{t("At least 8 characters")}</p>
                </div>
                <div className={styles.root_main_rules_list_item}>
                  <div className={styles.root_main_rules_list_item_img}>
                    <img src={hasUppercase ? done : dot} alt="dot" />
                  </div>
                  <p>{t("An uppercase letter")}</p>
                </div>
                <div className={styles.root_main_rules_list_item}>
                  <div className={styles.root_main_rules_list_item_img}>
                    <img src={hasLowercase ? done : dot} alt="dot" />
                  </div>
                  <p>{t("A lowercase letter")}</p>
                </div>
                <div className={styles.root_main_rules_list_item}>
                  <div className={styles.root_main_rules_list_item_img}>
                    <img src={hasDigit ? done : dot} alt="dot" />
                  </div>
                  <p>{t("A digit")}</p>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.root_footer}>
            <Button
              onClick={onUpdateClickHandler}
              text={t("Update")}
              classNames={styles.root_footer_button}
              isLoader={isLoader}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default UpdatePasswordForm;
